<template>
  <Teleport to="body">
  <div class="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-[60]" @click.self="$emit('close')">
    <div
      :class="[
        'bg-white shadow-xl overflow-hidden flex flex-col',
        isFullscreen
          ? 'fixed inset-0 w-screen h-screen max-h-screen rounded-none'
          : 'rounded-lg max-w-7xl w-full mx-4 h-[95vh]'
      ]"
    >
      <!-- Header -->
      <div class="flex justify-between items-center px-6 py-3 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
        <div class="flex items-center gap-4">
          <div class="p-3 bg-white rounded-lg shadow-sm">
            <FileText class="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h3 class="text-xl font-bold text-gray-900">Leave Request Documents</h3>
            <p class="text-sm text-gray-600 mt-1">
              {{ leaveRequest.employee_name || 'Employee' }} - {{ formatDate(leaveRequest.start_date) }}
              <span v-if="documents.length > 0" class="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                {{ documents.length }} file{{ documents.length !== 1 ? 's' : '' }}
              </span>
            </p>
          </div>
        </div>
        <div class="flex items-center gap-2">
          <button
            v-if="documents.length > 1"
            @click="downloadAllDocuments"
            :disabled="downloadingAll"
            class="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 disabled:opacity-50"
            title="Download all as ZIP"
          >
            <Download class="w-4 h-4" />
            {{ downloadingAll ? 'Preparing...' : 'Download All' }}
          </button>
          <button @click="$emit('close')" class="text-gray-400 hover:text-gray-600 transition-colors p-2">
            <X class="w-6 h-6" />
          </button>
        </div>
      </div>

      <!-- Content -->
      <div class="flex-1 overflow-hidden flex" style="min-height: 0;">
        <!-- Documents List (Left Panel) -->
        <div class="w-1/3 border-r border-gray-200 overflow-y-auto p-6">
          <!-- Loading State -->
          <div v-if="loading" class="flex items-center justify-center py-12">
            <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span class="ml-3 text-gray-600">Loading...</span>
          </div>

          <!-- Error State -->
          <div v-else-if="error" class="text-center py-12">
            <AlertCircle class="w-12 h-12 text-red-500 mx-auto mb-4" />
            <p class="text-gray-600 text-sm">{{ error }}</p>
            <button @click="loadDocuments" class="mt-4 px-4 py-2 text-blue-600 hover:text-blue-700 text-sm">
              Try Again
            </button>
          </div>

          <!-- Empty State -->
          <div v-else-if="documents.length === 0" class="text-center py-12">
            <FileText class="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p class="text-gray-600 text-sm">No documents attached</p>
          </div>

          <!-- Documents List -->
          <div v-else class="space-y-3">
            <div
              v-for="(doc, index) in documents"
              :key="doc.id"
              @click="viewDocument(doc)"
              :class="[
                'p-4 rounded-lg border-2 cursor-pointer transition-all group',
                selectedDocument?.id === doc.id
                  ? 'border-blue-500 bg-blue-50 shadow-md'
                  : 'border-gray-200 bg-white hover:bg-gray-50 hover:border-gray-300'
              ]"
            >
              <div class="flex items-start gap-3">
                <div class="flex-shrink-0 relative">
                  <!-- Document thumbnail/icon -->
                  <div v-if="isImage(doc) && blobUrlCache[doc.id]" class="w-16 h-16 bg-gray-100 rounded-lg overflow-hidden border border-gray-200">
                    <img
                      :src="blobUrlCache[doc.id]"
                      :alt="doc.originalName || doc.filename"
                      class="w-full h-full object-cover"
                      @error="handleThumbnailError"
                    />
                  </div>
                  <div v-else-if="isImage(doc)" class="w-16 h-16 bg-purple-50 rounded-lg flex items-center justify-center border border-purple-200">
                    <ImageIcon class="w-8 h-8 text-purple-500" />
                  </div>
                  <div v-else-if="isPDF(doc)" class="w-16 h-16 bg-red-50 rounded-lg flex items-center justify-center border border-red-200">
                    <FileText class="w-8 h-8 text-red-600" />
                  </div>
                  <div v-else class="w-16 h-16 bg-blue-50 rounded-lg flex items-center justify-center border border-blue-200">
                    <FileText class="w-8 h-8 text-blue-600" />
                  </div>
                  <div class="absolute -top-1 -right-1 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold">
                    {{ index + 1 }}
                  </div>
                </div>
                <div class="flex-1 min-w-0">
                  <p class="text-sm font-medium text-gray-900 truncate" :title="doc.originalName || doc.filename">
                    {{ doc.originalName || doc.filename }}
                  </p>
                  <div class="flex items-center gap-2 mt-1">
                    <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
                      {{ getFileExtension(doc) }}
                    </span>
                    <span class="text-xs text-gray-500">
                      {{ formatFileSize(doc.fileSizeBytes || doc.sizeBytes) }}
                    </span>
                  </div>
                  <p class="text-xs text-gray-500 mt-1">
                    {{ formatDate(doc.uploadedAt) }}
                  </p>
                  <p v-if="doc.uploadedBy" class="text-xs text-gray-500">
                    By {{ doc.uploadedBy.name || doc.uploadedBy.email }}
                  </p>
                </div>
              </div>
              <div class="flex gap-2 mt-3">
                <button
                  @click.stop="viewDocument(doc)"
                  class="flex-1 px-3 py-2 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-1.5 font-medium"
                >
                  <Eye class="w-3.5 h-3.5" />
                  View
                </button>
                <button
                  @click.stop="downloadDocument(doc)"
                  :disabled="downloading === doc.id"
                  class="flex-1 px-3 py-2 text-xs bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center justify-center gap-1.5 disabled:opacity-50 font-medium"
                >
                  <Download class="w-3.5 h-3.5" />
                  {{ downloading === doc.id ? 'Downloading...' : 'Download' }}
                </button>
                <button
                  v-if="canDeleteDocument"
                  @click.stop="confirmDeleteDocument(doc)"
                  :disabled="deleting === doc.id"
                  class="px-3 py-2 text-xs bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center disabled:opacity-50"
                  title="Delete document"
                >
                  <Trash2 class="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Document Viewer (Right Panel) -->
        <div class="flex-1 overflow-auto bg-gray-100 flex items-center justify-center">
          <!-- No Document Selected -->
          <div v-if="!selectedDocument" class="text-center p-12">
            <FileText class="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p class="text-gray-600">Select a document to view</p>
          </div>

          <!-- Loading Viewer -->
          <div v-else-if="viewerLoading" class="text-center p-12">
            <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p class="text-gray-600">Loading document...</p>
          </div>

          <!-- Viewer Error -->
          <div v-else-if="viewerError" class="text-center p-12">
            <AlertCircle class="w-16 h-16 text-red-500 mx-auto mb-4" />
            <p class="text-gray-600 mb-4">{{ viewerError }}</p>
            <div class="flex gap-3 justify-center">
              <button
                @click="viewDocument(selectedDocument)"
                class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                <Eye class="w-4 h-4" />
                Try Again
              </button>
              <button
                @click="downloadDocument(selectedDocument)"
                class="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center gap-2"
              >
                <Download class="w-4 h-4" />
                Download Instead
              </button>
            </div>
          </div>

          <!-- Document Viewer -->
          <div v-else-if="documentBlobUrl" class="w-full h-full flex flex-col">
            <!-- Viewer Controls Bar -->
            <div class="bg-gray-800 text-white px-4 py-3 flex items-center justify-between">
              <div class="flex items-center gap-4">
                <span class="text-sm font-medium truncate max-w-xs" :title="selectedDocument.originalName || selectedDocument.filename">
                  {{ selectedDocument.originalName || selectedDocument.filename }}
                </span>
                <span class="text-xs text-gray-400">
                  {{ formatFileSize(selectedDocument.fileSizeBytes || selectedDocument.sizeBytes) }}
                </span>
              </div>
              <div class="flex items-center gap-2">
                <!-- Navigation -->
                <div v-if="documents.length > 1" class="flex items-center gap-2 border-r border-gray-600 pr-3 mr-3">
                  <button
                    @click="navigateDocument('prev')"
                    :disabled="currentDocumentIndex === 0"
                    class="p-2 hover:bg-gray-700 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Previous document (←)"
                  >
                    <ChevronLeft class="w-4 h-4" />
                  </button>
                  <span class="text-sm">
                    {{ currentDocumentIndex + 1 }} / {{ documents.length }}
                  </span>
                  <button
                    @click="navigateDocument('next')"
                    :disabled="currentDocumentIndex === documents.length - 1"
                    class="p-2 hover:bg-gray-700 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Next document (→)"
                  >
                    <ChevronRight class="w-4 h-4" />
                  </button>
                </div>

                <!-- Zoom Controls (for images) -->
                <div v-if="isImage(selectedDocument)" class="flex items-center gap-2 border-r border-gray-600 pr-3 mr-3">
                  <button
                    @click="adjustZoom(-0.1)"
                    class="p-2 hover:bg-gray-700 rounded transition-colors"
                    title="Zoom out (-)"
                  >
                    <ZoomOut class="w-4 h-4" />
                  </button>
                  <span class="text-sm min-w-[4rem] text-center">{{ Math.round(imageZoom * 100) }}%</span>
                  <button
                    @click="adjustZoom(0.1)"
                    class="p-2 hover:bg-gray-700 rounded transition-colors"
                    title="Zoom in (+)"
                  >
                    <ZoomIn class="w-4 h-4" />
                  </button>
                  <button
                    @click="resetZoom"
                    class="p-2 hover:bg-gray-700 rounded transition-colors"
                    title="Reset zoom (0)"
                  >
                    <RotateCcw class="w-4 h-4" />
                  </button>
                </div>

                <!-- Rotate (for images) -->
                <div v-if="isImage(selectedDocument)" class="flex items-center gap-2 border-r border-gray-600 pr-3 mr-3">
                  <button
                    @click="rotateImage(-90)"
                    class="p-2 hover:bg-gray-700 rounded transition-colors"
                    title="Rotate left"
                  >
                    <RotateCcw class="w-4 h-4" />
                  </button>
                  <button
                    @click="rotateImage(90)"
                    class="p-2 hover:bg-gray-700 rounded transition-colors"
                    title="Rotate right"
                  >
                    <RotateCw class="w-4 h-4" />
                  </button>
                </div>

                <!-- Fullscreen -->
                <button
                  @click="toggleFullscreen"
                  class="p-2 hover:bg-gray-700 rounded transition-colors"
                  :title="isFullscreen ? 'Exit fullscreen' : 'Fullscreen'"
                >
                  <Maximize v-if="!isFullscreen" class="w-4 h-4" />
                  <Minimize v-else class="w-4 h-4" />
                </button>
              </div>
            </div>

            <!-- Viewer Content -->
            <div class="flex-1 overflow-auto bg-gray-900 p-4">
              <!-- PDF Viewer -->
              <iframe
                v-if="isPDF(selectedDocument)"
                :src="documentBlobUrl"
                class="w-full h-full rounded border border-gray-700 bg-white"
                title="Document Viewer"
              ></iframe>

              <!-- Image Viewer with Pan & Zoom -->
              <div
                v-else-if="isImage(selectedDocument)"
                class="w-full h-full flex items-center justify-center overflow-hidden cursor-move"
                @mousedown="startImageDrag"
                @mousemove="dragImage"
                @mouseup="stopImageDrag"
                @mouseleave="stopImageDrag"
                @wheel="handleWheel"
              >
                <img
                  ref="imageViewer"
                  :src="documentBlobUrl"
                  :alt="selectedDocument.filename"
                  :style="{
                    transform: `scale(${imageZoom}) rotate(${imageRotation}deg) translate(${imagePanX}px, ${imagePanY}px)`,
                    transition: isDragging ? 'none' : 'transform 0.2s ease-out',
                    maxWidth: 'none',
                    maxHeight: 'none',
                  }"
                  class="rounded-lg shadow-2xl select-none"
                  draggable="false"
                />
              </div>

              <!-- Unsupported Format -->
              <div v-else class="flex items-center justify-center h-full">
                <div class="text-center p-12 bg-gray-800 rounded-lg">
                  <AlertCircle class="w-16 h-16 text-yellow-500 mx-auto mb-4" />
                  <p class="text-gray-300 mb-4">
                    This file type cannot be previewed in the browser
                  </p>
                  <button
                    @click="downloadDocument(selectedDocument)"
                    class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 mx-auto"
                  >
                    <Download class="w-4 h-4" />
                    Download to View
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="flex justify-end gap-3 px-6 py-3 border-t border-gray-200">
        <button
          @click="$emit('close')"
          class="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
        >
          Close
        </button>
      </div>
    </div>
  </div>
  </Teleport>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import {
  X,
  FileText,
  Image as ImageIcon,
  Download,
  AlertCircle,
  Eye,
  Trash2,
  ZoomIn,
  ZoomOut,
  RotateCw,
  RotateCcw,
  Maximize,
  Minimize,
  ChevronLeft,
  ChevronRight
} from 'lucide-vue-next';
import adminService from '@/services/adminService';

const props = defineProps({
  leaveRequest: {
    type: Object,
    required: true
  }
});

const emit = defineEmits(['close', 'documentDeleted']);

const documents = ref([]);
const loading = ref(false);
const error = ref(null);
const downloading = ref(null);
const downloadingAll = ref(false);
const deleting = ref(null);
const selectedDocument = ref(null);
const viewerLoading = ref(false);
const documentBlobUrl = ref(null);
const viewerError = ref(null);
const blobUrlCache = ref({});

// Image viewer controls
const imageZoom = ref(1);
const imageRotation = ref(0);
const imagePanX = ref(0);
const imagePanY = ref(0);
const isDragging = ref(false);
const dragStartX = ref(0);
const dragStartY = ref(0);
const isFullscreen = ref(false);
const imageViewer = ref(null);

// Computed
const canDeleteDocument = computed(() => {
  // Only allow deletion if leave request is pending
  return props.leaveRequest.status === 'pending';
});

const currentDocumentIndex = computed(() => {
  return documents.value.findIndex(doc => doc.id === selectedDocument.value?.id);
});

const loadDocuments = async () => {
  loading.value = true;
  error.value = null;
  try {
    const response = await adminService.getLeaveDocuments(props.leaveRequest.id);
    if (response.success) {
      documents.value = response.data || [];
      // Auto-preview the first document so the viewer isn't empty on open
      if (documents.value.length > 0 && !selectedDocument.value) {
        viewDocument(documents.value[0]);
      }
      // Prefetch thumbnails for image documents
      documents.value.filter(isImage).forEach(doc => { getBlobUrl(doc).catch(() => {}); });
    } else {
      throw new Error(response.message || 'Failed to load documents');
    }
  } catch (err) {
    console.error('Error loading documents:', err);
    error.value = err.message || 'Failed to load documents';
  } finally {
    loading.value = false;
  }
};

const blobUrlRequests = new Map();

const getBlobUrl = (doc) => {
  if (blobUrlCache.value[doc.id]) return Promise.resolve(blobUrlCache.value[doc.id]);
  if (blobUrlRequests.has(doc.id)) return blobUrlRequests.get(doc.id);

  const request = adminService.getLeaveDocumentForViewing(doc.id)
    .then(result => {
      if (result.success) {
        blobUrlCache.value[doc.id] = result.url;
        return result.url;
      }
      throw new Error('Failed to load document');
    })
    .finally(() => {
      blobUrlRequests.delete(doc.id);
    });

  blobUrlRequests.set(doc.id, request);
  return request;
};

const downloadDocument = async (doc) => {
  downloading.value = doc.id;
  try {
    await adminService.downloadLeaveDocument(doc.id, doc.filename);
  } catch (err) {
    console.error('Error downloading document:', err);
    alert('Failed to download document. Please try again.');
  } finally {
    downloading.value = null;
  }
};

const viewDocument = async (doc) => {
  documentBlobUrl.value = null;
  viewerLoading.value = true;
  viewerError.value = null;
  selectedDocument.value = doc;

  try {
    // Fetch the document with authentication (cached per document)
    const url = await getBlobUrl(doc);
    // Ignore stale responses if the user switched documents meanwhile
    if (selectedDocument.value?.id === doc.id) {
      documentBlobUrl.value = url;
    }
  } catch (err) {
    console.error('Error loading document for viewing:', err);
    if (selectedDocument.value?.id === doc.id) {
      viewerError.value = 'Failed to load document. Please try again.';
    }
  } finally {
    if (selectedDocument.value?.id === doc.id) {
      viewerLoading.value = false;
    }
  }
};

const getDocumentUrl = () => {
  return documentBlobUrl.value || '';
};

const isPDF = (doc) => {
  if (!doc || !doc.mimeType) return false;
  return doc.mimeType === 'application/pdf';
};

const isImage = (doc) => {
  if (!doc || !doc.mimeType) return false;
  return doc.mimeType.startsWith('image/');
};

const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const getFileExtension = (doc) => {
  const filename = doc.originalName || doc.filename || '';
  const ext = filename.split('.').pop()?.toUpperCase();
  return ext || 'FILE';
};

const handleThumbnailError = (event) => {
  // Fallback icon if thumbnail fails to load
  event.target.style.display = 'none';
};

// Image zoom and pan functions
const adjustZoom = (delta) => {
  const newZoom = Math.max(0.1, Math.min(5, imageZoom.value + delta));
  imageZoom.value = newZoom;
};

const resetZoom = () => {
  imageZoom.value = 1;
  imageRotation.value = 0;
  imagePanX.value = 0;
  imagePanY.value = 0;
};

const rotateImage = (degrees) => {
  imageRotation.value = (imageRotation.value + degrees) % 360;
};

const handleWheel = (event) => {
  if (event.ctrlKey || event.metaKey) {
    event.preventDefault();
    const delta = event.deltaY > 0 ? -0.1 : 0.1;
    adjustZoom(delta);
  }
};

const startImageDrag = (event) => {
  if (event.button === 0) { // Left mouse button
    isDragging.value = true;
    dragStartX.value = event.clientX - imagePanX.value;
    dragStartY.value = event.clientY - imagePanY.value;
    event.preventDefault();
  }
};

const dragImage = (event) => {
  if (isDragging.value) {
    imagePanX.value = event.clientX - dragStartX.value;
    imagePanY.value = event.clientY - dragStartY.value;
  }
};

const stopImageDrag = () => {
  isDragging.value = false;
};

const toggleFullscreen = () => {
  isFullscreen.value = !isFullscreen.value;
};

// Document navigation
const navigateDocument = (direction) => {
  const currentIndex = currentDocumentIndex.value;
  if (direction === 'prev' && currentIndex > 0) {
    viewDocument(documents.value[currentIndex - 1]);
  } else if (direction === 'next' && currentIndex < documents.value.length - 1) {
    viewDocument(documents.value[currentIndex + 1]);
  }
};

// Bulk download
const downloadAllDocuments = async () => {
  downloadingAll.value = true;
  try {
    // Request backend to create a ZIP file with all documents
    const response = await adminService.downloadAllLeaveDocuments(props.leaveRequest.id);
    if (response.success) {
      console.log('All documents downloaded successfully');
    }
  } catch (err) {
    console.error('Error downloading all documents:', err);
    alert('Failed to download all documents. Downloading individually...');
    // Fallback: download each document individually
    for (const doc of documents.value) {
      try {
        await downloadDocument(doc);
        // Add a small delay between downloads
        await new Promise(resolve => setTimeout(resolve, 500));
      } catch (error) {
        console.error(`Failed to download ${doc.filename}:`, error);
      }
    }
  } finally {
    downloadingAll.value = false;
  }
};

// Delete document
const confirmDeleteDocument = (doc) => {
  if (confirm(`Are you sure you want to delete "${doc.originalName || doc.filename}"? This action cannot be undone.`)) {
    deleteDocument(doc);
  }
};

const deleteDocument = async (doc) => {
  deleting.value = doc.id;
  try {
    await adminService.deleteLeaveDocument(doc.id);

    // Remove from local list
    const index = documents.value.findIndex(d => d.id === doc.id);
    if (index > -1) {
      documents.value.splice(index, 1);
    }

    // If this was the selected document, clear selection
    if (selectedDocument.value?.id === doc.id) {
      selectedDocument.value = null;
      documentBlobUrl.value = null;
    }

    // Release the cached blob URL
    if (blobUrlCache.value[doc.id]) {
      window.URL.revokeObjectURL(blobUrlCache.value[doc.id]);
      delete blobUrlCache.value[doc.id];
    }

    emit('documentDeleted', doc);
    console.log('Document deleted successfully');
  } catch (err) {
    console.error('Error deleting document:', err);
    alert('Failed to delete document. Please try again.');
  } finally {
    deleting.value = null;
  }
};

// Keyboard shortcuts
const handleKeyPress = (event) => {
  if (event.key === 'Escape') {
    if (isFullscreen.value) {
      toggleFullscreen();
    } else {
      emit('close');
    }
    return;
  }

  if (!selectedDocument.value) return;

  switch (event.key) {
    case 'ArrowLeft':
      event.preventDefault();
      navigateDocument('prev');
      break;
    case 'ArrowRight':
      event.preventDefault();
      navigateDocument('next');
      break;
    case '+':
    case '=':
      if (isImage(selectedDocument.value)) {
        event.preventDefault();
        adjustZoom(0.1);
      }
      break;
    case '-':
    case '_':
      if (isImage(selectedDocument.value)) {
        event.preventDefault();
        adjustZoom(-0.1);
      }
      break;
    case '0':
      if (isImage(selectedDocument.value)) {
        event.preventDefault();
        resetZoom();
      }
      break;
  }
};

// Watch for document changes to reset zoom
watch(selectedDocument, () => {
  resetZoom();
});

onMounted(() => {
  loadDocuments();
  window.addEventListener('keydown', handleKeyPress);
});

onBeforeUnmount(() => {
  // Clean up cached blob URLs to prevent memory leaks
  Object.values(blobUrlCache.value).forEach(url => window.URL.revokeObjectURL(url));
  blobUrlCache.value = {};
  documentBlobUrl.value = null;
  window.removeEventListener('keydown', handleKeyPress);
});
</script>
